import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect, useSelector } from 'react-redux';
import Expander from '@starbucks-web/pattern-library/lib/components/expander';
import Toggle from '@starbucks-web/pattern-library/lib/components/toggle';

import Frap from 'shared/app/components/frap-with-messages';
import FrapContainer from 'shared/app/components/frap-container';
import MonetaryAmountSelect from 'shared/app/components/monetary-amount-select';
import PaymentInstrumentSelect from 'shared/app/components/payment-instrument-select';
import {
  allAutoReloadPaymentInstrumentsSelector,
  paymentServicesPendingSelector,
} from 'shared/app/bundles/wallet';

import { svcMessages } from 'shared/app/messages';
import messages from './messages';
import { autoReloadProfileSelector } from '../../state/selectors';

const tokenizedPaymentSources = ['googlepay', 'applepay'];

const AutoReloadForm = ({
  buttonMessage,
  fields,
  inProgress,
  intl: { formatMessage },
  paymentInstruments,
  paymentServicesPending,
  svcReloadOptions,
  triggerAmountOptions,
}) => {
  const autoReloadProfile = useSelector(autoReloadProfileSelector);

  // Auto-reload Payment instruments such as Apple Pay and Google Pay
  // cannot be re-used for different SVC cards
  const paymentTypes = paymentInstruments.reduce(
    (acc, pi) => {
      const isPiTokenized = tokenizedPaymentSources.includes(
        pi?.paymentSource?.toLowerCase()
      );
      const autoReloadPi =
        pi.paymentInstrumentId === autoReloadProfile?.paymentMethodId;
      if (isPiTokenized && autoReloadPi) {
        acc.tokenizedPaymentInstrument.push(pi);
      } else if (!isPiTokenized) {
        acc.nonTokenizedPaymentInstruments.push(pi);
      }
      return acc;
    },
    { tokenizedPaymentInstrument: [], nonTokenizedPaymentInstruments: [] }
  );

  const paymentInstrumentsList = [
    ...paymentTypes.tokenizedPaymentInstrument,
    ...paymentTypes.nonTokenizedPaymentInstruments,
  ];

  return (
    <div className="pt4">
      <label className="flex justify-between items-center mb4 pr1">
        <span className="text-sm ml8">
          <FormattedMessage {...svcMessages.autoReloadLabel} />
        </span>

        <Toggle
          {...fields?.enabled?.input}
          className="flex-shrink-none ml2"
          data-e2e="toggleAutoReload"
        />
      </label>

      <Expander isExpanded={fields.enabled.input.checked}>
        <div className="pl8 pr1">
          <MonetaryAmountSelect
            amounts={svcReloadOptions}
            label={formatMessage(messages.amountLabel)}
            {...fields?.amount?.input}
          />
          <MonetaryAmountSelect
            amounts={triggerAmountOptions}
            label={formatMessage(messages.triggerAmountLabel)}
            {...fields?.triggerAmount?.input}
          />
        </div>
        <div className="pr1">
          <PaymentInstrumentSelect
            disabled={paymentServicesPending}
            paymentInstruments={paymentInstrumentsList}
            {...fields?.paymentInstrument?.input}
          />
        </div>
      </Expander>

      <FrapContainer
        addSpacingForMultipleChildren={false}
        animated={false}
        relativeAbove="alwaysRelative"
      >
        <Frap
          data-e2e="saveAutoReload"
          loading={inProgress || paymentServicesPending}
          type="submit"
        >
          <FormattedMessage {...buttonMessage} />
        </Frap>
      </FrapContainer>
    </div>
  );
};

AutoReloadForm.propTypes = {
  fields: PropTypes.object,
  onError: PropTypes.func,
  onFieldsStateChange: PropTypes.func,
  onSubmit: PropTypes.func,
};

const select = (state) => ({
  paymentInstruments: allAutoReloadPaymentInstrumentsSelector(state),
  paymentServicesPending: paymentServicesPendingSelector(state),
});

export default injectIntl(connect(select)(AutoReloadForm));
